import React from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import type { FieldValues, Path } from "react-hook-form";
import type { Plan } from "client/api-v2";
import { IntLimitFeatureForm } from "components/features/IntLimitFeatureForm";
import { OverrunLimitFeatureForm } from "components/features/OverrunLimitFeatureForm";
import { Fieldset } from "components/form/Fieldset";
import type { PlanWithConfigurableInclusions } from "../../types/plan-extensions";
import {
  isPlanInclusionConfigurable,
  getPlanInclusionType,
  formatInclusionName,
  getPlanInclusionConfiguration,
} from "../../utils/plan-utils";
import { ReadOnlyEntitlementField } from "./ReadOnlyEntitlementField";
import { SimpleMachineLimitForm } from "./SimpleMachineLimitForm";

interface EntitlementFieldProps {
  fieldKey: string;
  value: unknown;
  plan: Plan | PlanWithConfigurableInclusions | undefined;
  disabled?: boolean;
}

export function EntitlementField({ fieldKey, value, plan, disabled }: EntitlementFieldProps) {
  const { control } = useFormContext();
  const isConfigurable = isPlanInclusionConfigurable(plan, fieldKey);
  const fieldType = getPlanInclusionType(plan, fieldKey, value);
  const displayName = formatInclusionName(fieldKey);
  const config = getPlanInclusionConfiguration(plan, fieldKey);
  const fieldPath: Path<FieldValues> = `entitlements.${fieldKey}`;

  // If not configurable or disabled, show read-only field
  if (!isConfigurable || disabled) {
    return (
      <ReadOnlyEntitlementField
        name={displayName}
        value={value}
        fieldType={fieldType || "int"}
        description={config?.description}
        isConfigurable={isConfigurable}
      />
    );
  }

  // Render editable field based on type
  switch (fieldType) {
    case "boolean":
      return (
        <Fieldset title={displayName}>
          <Controller
            name={fieldPath}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label="Enabled"
              />
            )}
          />
        </Fieldset>
      );

    case "intOrUnlimited":
      return (
        <IntLimitFeatureForm<FieldValues>
          fieldName={fieldPath}
          limitName={displayName}
          limitItemName={fieldKey.toLowerCase()}
          disabled={disabled}
        />
      );

    case "intWithOverrun":
      // Special handling for machines field
      if (fieldKey === "machines") {
        return (
          <SimpleMachineLimitForm<FieldValues>
            fieldName={fieldPath}
            limitName={displayName}
            limitItemName="machines"
            disabled={disabled}
          />
        );
      }
      // For other fields with overrun
      return (
        <OverrunLimitFeatureForm<FieldValues>
          fieldName={fieldPath}
          limitName={displayName}
          limitItemName={fieldKey.toLowerCase()}
          allowZero={true}
          disabled={disabled}
        />
      );

    case "int":
      // For simple integer fields, we can use IntLimitFeatureForm without unlimited option
      return (
        <IntLimitFeatureForm<FieldValues>
          fieldName={fieldPath}
          limitName={displayName}
          limitItemName={fieldKey.toLowerCase()}
          disabled={disabled}
        />
      );

    default:
      // Fallback to read-only for unknown types
      return (
        <ReadOnlyEntitlementField
          name={displayName}
          value={value}
          fieldType="int"
          description={config?.description}
          isConfigurable={false}
        />
      );
  }
}
